import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Input,
  List,
  Modal,
  Progress,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import {
  AppstoreAddOutlined,
  BookOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  FileTextOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { AxiosError } from "axios";
import {
  getIngestionJob,
  startIngestion,
  validateDocument,
  getPrompts,
} from "../../../services/content-ingestion/content-ingestion.service";
import {
  ACTIVITIES_HAVE_ALTERNATIVES,
  AlternativesActivitySummary,
  CHANGED_IN_PLATFORM,
  ConflictedAlternativeActivity,
  IngestErrorResponse,
  IngestionFlags,
  IngestionJob,
  IngestionResult,
  OnExistingAlternatives,
  PromptsResponse,
  STRUCTURE_MISMATCH,
  TARGET_HAS_LESSONS,
  ValidateResponse,
} from "../../../services/content-ingestion/content-ingestion.types";
import ChangedInPlatformModal, {
  changedReasons,
} from "../HubSubida/ChangedInPlatformModal";
import type {
  ChangedAnswer,
  ChangedPrompt,
  ChangedReasons,
} from "../HubSubida/hubTypes";
import { downloadPlatformVersion } from "../HubSubida/platformDownload";

const { Title, Text, Paragraph } = Typography;

const DOC_ID_REGEX = /\/document\/(?:u\/\d+\/)?d\/([^/]+)/;

/**
 * The 409 message names the target and its lesson count, then closes with the
 * "delete them first" instruction. That instruction is the right thing to show
 * when the admin cancels, but it contradicts a dialog offering to append, so
 * the dialog keeps only the factual half. Falls back to the whole message if
 * the API ever stops emitting that closing sentence.
 */
function targetFacts(message: string): string {
  return message.split("Debe eliminar")[0].trim() || message;
}

function extractDocumentId(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";

  // First try to parse the input as a URL and extract the document ID from the path
  // (e.g. /document/u/0/d/<id>/... ) or from the query param `id` (e.g. open?id=<id>).
  try {
    const url = new URL(trimmed);
    const pathMatch = url.pathname.match(DOC_ID_REGEX);
    if (pathMatch) return pathMatch[1];

    const queryId = url.searchParams.get("id");
    if (queryId) return queryId;
  } catch (err) {
    // Ignore invalid URLs (e.g., when the user inputs only the doc ID).
    // If something else goes wrong (unlikely), rethrow so it can be noticed.
    if (!(err instanceof TypeError)) throw err;
  }

  // Fallback: allow pasting just the ID or a URL-like string that matches the regex
  const match = trimmed.match(DOC_ID_REGEX);
  return match ? match[1] : trimmed;
}

/**
 * SCRUM-1532: an OD downloaded from the platform fails the normal validation
 * with errors that only block appending (header resources, «PDF N ya existe»).
 * True when the doc validates clean as a replacement with the same IDs; a
 * failed call counts as not clean, so the page shows today's result.
 */
async function replaceValidationIsClean(documentId: string): Promise<boolean> {
  try {
    const result = await validateDocument(documentId, { replace: true });
    return !result.hasErrors;
  } catch {
    return false;
  }
}

/** Severity chip for a validation issue. Unknown severities fall back to the AVISO style. */
function renderIssueSeverityTag(severity: string): React.ReactElement {
  if (severity === "error") {
    return (
      <Tag color="error" style={{ minWidth: 80, textAlign: "center" }}>
        ERROR
      </Tag>
    );
  }
  if (severity === "info") {
    return (
      <Tag color="blue" style={{ minWidth: 80, textAlign: "center" }}>
        INFO
      </Tag>
    );
  }
  return (
    <Tag
      color="warning"
      icon={<WarningOutlined />}
      style={{ minWidth: 80, textAlign: "center" }}
    >
      AVISO
    </Tag>
  );
}

/** SCRUM-1532: a replace creates nothing; it updates the course's lessons in place. */
const replacedSummary = (count: number): string =>
  `¡Subida completada! Se ${count === 1 ? "sustituyó 1 lección" : `sustituyeron ${count} lecciones`}, con los mismos IDs.`;

const SubidorSupersonico: React.FC = () => {
  const { token } = theme.useToken();
  const [docInput, setDocInput] = useState("");
  const [validating, setValidating] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [ingestProgress, setIngestProgress] =
    useState<IngestionJob | null>(null);
  const [validateResult, setValidateResult] = useState<ValidateResponse | null>(
    null,
  );
  const [ingestResult, setIngestResult] = useState<IngestionResult | null>(
    null,
  );
  const [ingestErrors, setIngestErrors] = useState<string[]>([]);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<PromptsResponse | null>(null);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [readingConfirmed, setReadingConfirmed] = useState(false);
  const [alternativesConfirmed, setAlternativesConfirmed] = useState(false);
  // SCRUM-1532: the validation's errors only block appending; the doc can
  // still replace the course's content with the same IDs.
  const [replaceAvailable, setReplaceAvailable] = useState(false);
  // Holds the API's 409 message while the admin decides whether to append.
  const [appendPrompt, setAppendPrompt] = useState<string | null>(null);
  // SCRUM-1532: the course was edited in the platform after its last upload;
  // `courseId` is null when the API does not name it (nothing to download).
  const [changedConflict, setChangedConflict] = useState<{
    prompt: ChangedPrompt;
    courseId: number | null;
  } | null>(null);
  // What downloading the platform's version came to.
  const [platformDownload, setPlatformDownload] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [downloadingPlatform, setDownloadingPlatform] = useState(false);
  // Holds the ACTIVITIES_HAVE_ALTERNATIVES 409 payload while the admin makes
  // the one global replace/skip decision.
  const [alternativesConflict, setAlternativesConflict] = useState<{
    message: string;
    activities: ConflictedAlternativeActivity[];
  } | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
      setGlobalError(null);
    } catch (err) {
      setGlobalError(
        "No se pudo copiar al portapapeles. Asegúrate de que tu navegador permita el acceso al portapapeles.",
      );
      console.warn("Clipboard copy failed", err);
    }
  };

  const handleLoadPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const result = await getPrompts();
      setPrompts(result);
      setGlobalError(null);
    } catch (err) {
      console.error("Error loading prompts", err);
      setGlobalError(
        "No se pudieron cargar los prompts. Intenta de nuevo más tarde.",
      );
    } finally {
      setLoadingPrompts(false);
    }
  };
  const documentId = extractDocumentId(docInput);
  const hasValidInput = documentId.length > 0;
  // A Lectura Primaria doc additionally requires the admin to explicitly
  // confirm the resolved target program before Subir is enabled.
  const isReadingDoc = validateResult?.reading !== undefined;
  const readingGateOk =
    !isReadingDoc ||
    (validateResult?.reading?.program != null && readingConfirmed);
  // An alternatives doc mirrors the reading gate: the admin must review the
  // summary panel and explicitly confirm it before the upload is enabled.
  const isAlternativesDoc = validateResult?.alternatives !== undefined;
  const alternativesGateOk =
    !isAlternativesDoc ||
    (validateResult?.alternatives?.course != null && alternativesConfirmed);
  const canIngest =
    validateResult !== null &&
    !validateResult.hasErrors &&
    !ingesting &&
    readingGateOk &&
    alternativesGateOk;

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        clearTimeout(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!ingesting) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [ingesting]);

  const handleValidate = async () => {
    if (ingesting) return;
    setValidating(true);
    setValidateResult(null);
    setIngestResult(null);
    setIngestErrors([]);
    setGlobalError(null);
    setReadingConfirmed(false);
    setAlternativesConfirmed(false);
    setReplaceAvailable(false);
    try {
      const result = await validateDocument(documentId);
      // Only a doc with errors is validated again, and both results show
      // together.
      setReplaceAvailable(
        result.hasErrors && (await replaceValidationIsClean(documentId)),
      );
      setValidateResult(result);
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>;
      setGlobalError(
        axiosErr.response?.data?.message ??
          "No se pudo acceder al documento. Verifica el ID o la URL e intenta de nuevo.",
      );
    } finally {
      setValidating(false);
    }
  };

  const STATUS_LABELS: Record<string, string> = {
    PENDING: "Iniciando...",
    READING_DOCUMENT: "Leyendo documento...",
    VALIDATING: "Validando estructura...",
    BUILDING_REGISTRY: "Procesando recursos...",
    INGESTING_LESSONS: "Ingresando lecciones...",
    INGESTING_READING: "Ingresando lectura primaria...",
    INGESTING_ALTERNATIVES: "Ingresando actividades alternativas...",
    PROCESSING_VIDEOS: "Procesando videos...",
    COMPLETED: "¡Ingesta completada!",
    FAILED: "Error durante la ingesta.",
  };

  function stepLabel(job: IngestionJob): string {
    if (
      job.status === "INGESTING_LESSONS" &&
      job.current_lesson != null &&
      job.total_lessons != null
    ) {
      return `Ingresando lección ${job.current_lesson} de ${job.total_lessons}${job.message ? ` — ${job.message}` : ""}`;
    }
    if (job.status === "PROCESSING_VIDEOS") {
      const videoMsg = job.message?.match(/^(Procesando video \d+ de \d+)/)?.[1];
      return videoMsg ?? "Procesando videos...";
    }
    if (job.status === "INGESTING_ALTERNATIVES" && job.message) {
      // The worker publishes the current "Lección N > Título" as the message.
      return `${STATUS_LABELS.INGESTING_ALTERNATIVES} — ${job.message}`;
    }
    return STATUS_LABELS[job.status] ?? job.status;
  }

  const runIngestion = async (
    append: boolean,
    onExistingAlternatives?: OnExistingAlternatives,
    flags?: IngestionFlags,
  ) => {
    setIngesting(true);
    setIngestResult(null);
    setIngestProgress(null);
    setIngestErrors([]);
    setGlobalError(null);
    setAppendPrompt(null);
    setAlternativesConflict(null);
    setChangedConflict(null);
    setPlatformDownload(null);

    let jobId: number;
    try {
      // Two-arg call while no conflict decision exists, so the lessons/reading
      // flows keep their exact historical call shape.
      const job = flags
        ? await startIngestion(
            documentId,
            append,
            onExistingAlternatives,
            flags,
          )
        : onExistingAlternatives
          ? await startIngestion(documentId, append, onExistingAlternatives)
          : await startIngestion(documentId, append);
      jobId = job.jobId;
    } catch (err) {
      // The CHANGED_IN_PLATFORM 409 also says why the OD changed.
      const axiosErr = err as AxiosError<IngestErrorResponse & ChangedReasons>;
      if (axiosErr.response?.status === 400 && axiosErr.response.data?.errors) {
        setIngestErrors(axiosErr.response.data.errors);
      } else if (
        axiosErr.response?.status === 409 &&
        axiosErr.response.data?.error === TARGET_HAS_LESSONS
      ) {
        // The only conflict the admin can resolve from here: ask whether to add
        // these lessons to the existing ones instead of failing outright.
        setAppendPrompt(
          axiosErr.response.data.message ??
            "El curso ya tiene lecciones asociadas.",
        );
      } else if (
        axiosErr.response?.status === 409 &&
        axiosErr.response.data?.error === ACTIVITIES_HAVE_ALTERNATIVES
      ) {
        // Resolvable too: the admin decides once, globally, whether the doc's
        // alternatives replace the existing sets or the conflicted activities
        // are skipped.
        setAlternativesConflict({
          message:
            axiosErr.response.data.message ??
            "Hay actividades que ya tienen alternativas configuradas.",
          activities: axiosErr.response.data.activities ?? [],
        });
      } else if (
        axiosErr.response?.status === 409 &&
        axiosErr.response.data?.error === CHANGED_IN_PLATFORM
      ) {
        // Replacing would lose the platform's edits: the admin decides.
        const data = axiosErr.response.data;
        setChangedConflict({
          prompt: {
            part: "doc",
            clave: data.clave ?? null,
            title: data.title ?? "",
            lastUploadAt: data.lastUploadAt ?? null,
            lastPlatformEditAt: data.lastPlatformEditAt ?? null,
            ...changedReasons(data),
          },
          courseId: data.courseId ?? null,
        });
      } else if (
        axiosErr.response?.status === 422 &&
        axiosErr.response.data?.error === STRUCTURE_MISMATCH
      ) {
        // Replacing pairs lessons, activities and blocks by position.
        setGlobalError(
          axiosErr.response.data.message ??
            "La OD no tiene la misma estructura que la de plataforma.",
        );
      } else if (axiosErr.response?.status === 409) {
        setGlobalError(
          axiosErr.response.data?.message ??
            "El curso ya tiene lecciones asociadas. Debes eliminar o desconectar las lecciones existentes antes de subir un nuevo documento.",
        );
      } else if (axiosErr.response?.status === 404) {
        setGlobalError(
          "El curso indicado en el documento no existe en la base de datos. Verifica el campo 'Curso #: N'.",
        );
      } else {
        setGlobalError(
          "Ocurrió un error durante la subida. Revisá los logs del servidor.",
        );
      }
      setIngesting(false);
      return;
    }

    // Clear any in-flight poll before starting a new one
    if (pollIntervalRef.current !== null) {
      clearTimeout(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    const TERMINAL_STATUSES = ["COMPLETED", "FAILED"];

    const schedulePoll = () => {
      pollIntervalRef.current = setTimeout(async () => {
        try {
          const job = await getIngestionJob(jobId);
          setIngestProgress(job);
          if (TERMINAL_STATUSES.includes(job.status)) {
            pollIntervalRef.current = null;
            setIngesting(false);
            if (job.status === "COMPLETED" && job.result) {
              setIngestResult(job.result);
            } else if (job.status === "FAILED") {
              setGlobalError(
                job.error_message ?? "Ocurrió un error durante la ingesta.",
              );
            }
          } else {
            schedulePoll();
          }
        } catch {
          pollIntervalRef.current = null;
          setIngesting(false);
          setGlobalError("Conexión perdida con el servidor.");
        }
      }, 3000);
    };

    schedulePoll();
  };

  const handleIngest = () => runIngestion(false);

  const handleConfirmAppend = () => {
    setAppendPrompt(null);
    void runIngestion(true);
  };

  /** SCRUM-1532: the doc replaces the course's content in place, same IDs. */
  const handleReplaceLessons = () => {
    setAppendPrompt(null);
    void runIngestion(false, undefined, { replace: true });
  };

  const handleChangedAnswer = async (answer: ChangedAnswer) => {
    const conflict = changedConflict;
    setChangedConflict(null);
    if (!conflict || answer === "cancel") return;
    if (answer === "force") {
      void runIngestion(false, undefined, { replace: true, force: true });
      return;
    }
    if (conflict.courseId === null) return;
    setDownloadingPlatform(true);
    const download = await downloadPlatformVersion(conflict.courseId);
    setDownloadingPlatform(false);
    setPlatformDownload(
      download.ok
        ? { ok: true, message: "Se descargó la versión de plataforma." }
        : {
            ok: false,
            message: `No se pudo descargar la versión de plataforma: ${download.error}`,
          },
    );
  };

  /** Cancelling puts the admin back where they were before: the original rule. */
  const handleCancelAppend = () => {
    const message = appendPrompt;
    setAppendPrompt(null);
    setGlobalError(message);
  };

  const handleReplaceExisting = () => {
    setAlternativesConflict(null);
    void runIngestion(false, "replace");
  };

  const handleSkipConflicted = () => {
    setAlternativesConflict(null);
    void runIngestion(false, "skip");
  };

  /** Cancelling surfaces the API's own conflict explanation, like the append flow. */
  const handleCancelAlternativesConflict = () => {
    const message = alternativesConflict?.message ?? null;
    setAlternativesConflict(null);
    setGlobalError(message);
  };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 16px" }}>
      <Space align="center" style={{ marginBottom: 8 }}>
        <RocketOutlined style={{ fontSize: 28, color: "var(--bo-primary)" }} />
        <Title level={2} style={{ margin: 0 }}>
          Subidor Supersónico
        </Title>
      </Space>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Pega la URL o el ID del documento de Google Drive con la plantilla de
        contenido, valida los errores y luego ejecuta la subida.
      </Paragraph>

      <Space style={{ marginBottom: 24 }}>
        <Button
          icon={<FileTextOutlined />}
          href="https://docs.google.com/document/d/1CrrfaBwV54-rYndZqLb34qDmprF4yknzaMPSzBA6VTs/edit?tab=t.0"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ejemplo Real
        </Button>
        <Button
          icon={<FileTextOutlined />}
          href="https://docs.google.com/document/d/1a6vPrGX9ElqpdU_tW0VkK_ML_CgE0JmHHigNYIAGOHM/edit?tab=t.0"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ejemplo Primaria
        </Button>
        <Button
          icon={<FileTextOutlined />}
          href="https://docs.google.com/document/d/1GV8yQx2ciA-lPs1kCbKUST3j38NZ6EIAwTCynL6JtCY/edit?tab=t.0"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ejemplo Preescolar
        </Button>
        <Button
          icon={<FileTextOutlined />}
          href="https://docs.google.com/document/d/1LvEsT_pGG4G6F4iPud__d7wBWIW19O_BnNuSZbSyQTY/edit?tab=t.0"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ejemplo Proyectos
        </Button>
      </Space>

      {/* Input */}
      <Card style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="https://docs.google.com/document/d/… o el ID directo"
            value={docInput}
            onChange={(e) => {
              setDocInput(e.target.value);
              setValidateResult(null);
              setIngestResult(null);
              setIngestErrors([]);
              setGlobalError(null);
              setReadingConfirmed(false);
              setAlternativesConfirmed(false);
              setReplaceAvailable(false);
            }}
            onPressEnter={
              hasValidInput && !ingesting ? handleValidate : undefined
            }
            size="large"
            allowClear
          />
          <Button
            type="default"
            size="large"
            icon={<SafetyCertificateOutlined />}
            loading={validating}
            disabled={!hasValidInput || ingesting}
            onClick={handleValidate}
          >
            Validar plantilla
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<RocketOutlined />}
            loading={ingesting}
            disabled={!canIngest}
            onClick={handleIngest}
          >
            {/* Mode-specific label: an alternatives upload must never look
                like a course upload. */}
            {isAlternativesDoc ? "Subir alternativas" : "Subir"}
          </Button>
        </Space.Compact>
        {hasValidInput && docInput !== documentId && (
          <Text
            type="secondary"
            style={{ fontSize: 12, marginTop: 8, display: "block" }}
          >
            Document ID detectado: <Text code>{documentId}</Text>
          </Text>
        )}
      </Card>

      {/* Stay-on-page warning */}
      {ingesting && (
        <Alert
          type="warning"
          showIcon
          message={
            <Text strong>
              ⚠️ No cierres ni abandones esta página hasta que la subida
              termine. Si sales ahora perderás el estado de la carga.
            </Text>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Ingestion progress */}
      {ingesting && ingestProgress && (
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text>{stepLabel(ingestProgress)}</Text>
            <Progress
              percent={ingestProgress.progress}
              status={ingestProgress.status === "FAILED" ? "exception" : "active"}
              strokeColor="var(--bo-primary)"
            />
            {ingestProgress.status === "PROCESSING_VIDEOS" && (
              <>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Progreso del video actual:
                </Text>
                <Progress
                  percent={parseInt(
                    ingestProgress.message?.match(/(\d+)%/)?.[1] ?? "0",
                    10,
                  )}
                  status="active"
                  strokeColor={token.colorWarning}
                />
              </>
            )}
          </Space>
        </Card>
      )}

      {/* Global error */}
      {globalError && (
        <Alert
          type="error"
          message={globalError}
          showIcon
          // Worker errors and the append conflict are multi-line; without this
          // every line collapses into one paragraph.
          style={{ marginBottom: 16, whiteSpace: "pre-line" }}
        />
      )}

      {/* SCRUM-1532: the platform's version, downloaded instead of uploading. */}
      {downloadingPlatform && (
        <Alert
          type="info"
          showIcon
          message="Descargando la versión de plataforma…"
          style={{ marginBottom: 16 }}
        />
      )}
      {platformDownload && (
        <Alert
          type={platformDownload.ok ? "success" : "error"}
          showIcon
          message={platformDownload.message}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Lectura Primaria: resolved program + explicit admin confirmation.
          Only rendered once validation has zero errors — an unresolved
          program always raises an ERROR issue, so the panel would never
          have a program to confirm while errors are present. In that
          case the error list below explains the situation instead. */}
      {validateResult?.reading !== undefined &&
        !validateResult.hasErrors &&
        validateResult.reading.program && (
          <Card
            title={
              <Space>
                <Tag color="purple" icon={<BookOutlined />}>
                  Lectura Primaria
                </Tag>
                <Text>Programa destino</Text>
              </Space>
            }
            style={{ marginBottom: 16, borderColor: "var(--bo-primary)" }}
          >
            <Paragraph style={{ marginBottom: 12 }}>
              Este documento fue identificado como una{" "}
              <Text strong>Lectura Primaria</Text> y se vinculará al
              siguiente programa:
            </Paragraph>
            <Space direction="vertical" style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16 }}>
                {validateResult.reading.program.name}
              </Text>
              <Text type="secondary">
                ID de programa:{" "}
                <Text code>{validateResult.reading.program.id}</Text>
              </Text>
            </Space>
            <br />
            <Checkbox
              checked={readingConfirmed}
              onChange={(e) => setReadingConfirmed(e.target.checked)}
            >
              Confirmo que el programa destino es correcto
            </Checkbox>
          </Card>
        )}

      {/* Actividades alternativas: pre-ingest summary + explicit admin
          confirmation. Mirrors the Lectura Primaria panel: only rendered once
          validation has zero errors — an unresolved course always raises an
          ERROR issue, so the error list below explains that case instead. */}
      {validateResult?.alternatives !== undefined &&
        !validateResult.hasErrors &&
        validateResult.alternatives.course && (
          <Card
            title={
              <Space>
                <Tag color="geekblue" icon={<AppstoreAddOutlined />}>
                  Actividades alternativas
                </Tag>
                <Text>Resumen de la plantilla</Text>
              </Space>
            }
            style={{ marginBottom: 16, borderColor: "var(--bo-primary)" }}
          >
            <Paragraph style={{ marginBottom: 12 }}>
              Este documento fue identificado como una plantilla de{" "}
              <Text strong>actividades alternativas</Text>. No se crearán
              lecciones nuevas: las alternativas se vincularán a actividades
              manipulativas existentes del siguiente curso:
            </Paragraph>
            <Space direction="vertical" style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16 }}>
                {validateResult.alternatives.course.title}
              </Text>
              <Text type="secondary">
                ID de curso:{" "}
                <Text code>{validateResult.alternatives.course.id}</Text>
              </Text>
            </Space>
            <Table<AlternativesActivitySummary>
              size="small"
              pagination={false}
              rowKey={(a) => `${a.lesson_position}-${a.activity_title}`}
              dataSource={validateResult.alternatives.activities}
              style={{ marginBottom: 16 }}
              columns={[
                {
                  title: "Lección",
                  dataIndex: "lesson_position",
                  width: 90,
                },
                {
                  title: "Actividad",
                  dataIndex: "activity_title",
                },
                {
                  title: "Alternativas en el documento",
                  dataIndex: "alternatives_in_document",
                  width: 130,
                },
                {
                  title: "Alternativas existentes",
                  dataIndex: "existing_alternatives",
                  width: 130,
                  render: (existing: number) =>
                    existing > 0 ? (
                      <Tag color="warning" icon={<WarningOutlined />}>
                        {existing} {existing === 1 ? "existente" : "existentes"}
                      </Tag>
                    ) : (
                      <Text type="secondary">0</Text>
                    ),
                },
              ]}
            />
            {validateResult.alternatives.activities.some(
              (a) => a.existing_alternatives > 0,
            ) && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Algunas actividades ya tienen alternativas configuradas. Al subir, deberás decidir si se reemplazan o se conservan."
              />
            )}
            <Checkbox
              checked={alternativesConfirmed}
              onChange={(e) => setAlternativesConfirmed(e.target.checked)}
            >
              Confirmo que revisé el resumen y que las alternativas se aplicarán
              a las actividades listadas
            </Checkbox>
          </Card>
        )}

      {/* Validation results */}
      {validateResult && (
        <Card
          title={
            <Space>
              {validateResult.hasErrors ? (
                <Tag color="error">Con errores</Tag>
              ) : validateResult.hasWarnings ? (
                <Tag color="warning">Con advertencias</Tag>
              ) : (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Sin errores
                </Tag>
              )}
              <Text>Resultado de la validación</Text>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          {validateResult.issues.length === 0 ? (
            <Alert
              type="success"
              message="La plantilla no tiene errores ni advertencias. Puedes proceder con la subida."
              showIcon
            />
          ) : (
            <List
              size="small"
              dataSource={validateResult.issues}
              renderItem={(item) => (
                <List.Item>
                  <Space align="start" style={{ width: "100%" }}>
                    {renderIssueSeverityTag(item.severity)}
                    <div>
                      {item.location && (
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 11,
                            display: "block",
                            marginBottom: 2,
                          }}
                        >
                          {item.location}
                        </Text>
                      )}
                      <Text code style={{ fontSize: 12 }}>
                        {item.text}
                      </Text>
                      <br />
                      <Text>{item.issue}</Text>
                    </div>
                  </Space>
                </List.Item>
              )}
            />
          )}
          {/* SCRUM-1532: appending stays blocked; replacing is offered. */}
          {replaceAvailable && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message="Estos errores sólo impiden agregar lecciones. Puedes reemplazar el contenido del curso con los mismos IDs."
              action={
                <Button
                  size="small"
                  disabled={ingesting}
                  onClick={handleReplaceLessons}
                >
                  Reemplazar (mismos IDs)
                </Button>
              }
            />
          )}
        </Card>
      )}

      {/* Ingest errors (400) */}
      {ingestErrors.length > 0 && (
        <Card
          title={
            <Text type="danger">
              La subida fue rechazada — errores en la plantilla
            </Text>
          }
          style={{ marginBottom: 16 }}
        >
          <List
            size="small"
            dataSource={ingestErrors}
            renderItem={(msg) => (
              <List.Item>
                <Text type="danger">{msg}</Text>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Ingestion success */}
      {ingestResult && (
        <Card>
          {ingestResult.alternatives_created != null ||
          ingestResult.activities_matched != null ? (
            <>
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message={
                  <Text strong>
                    ¡Subida completada!{" "}
                    {ingestResult.alternatives_created === 1
                      ? "Se creó 1 alternativa"
                      : `Se crearon ${
                          ingestResult.alternatives_created ?? 0
                        } alternativas`}{" "}
                    para {ingestResult.activities_matched ?? 0}{" "}
                    {ingestResult.activities_matched === 1
                      ? "actividad"
                      : "actividades"}
                    .
                  </Text>
                }
                showIcon
                style={{ marginBottom: 16 }}
              />

              {(ingestResult.replaced_count ?? 0) > 0 && (
                <Paragraph>
                  Se reemplazaron las alternativas existentes de{" "}
                  {ingestResult.replaced_count}{" "}
                  {ingestResult.replaced_count === 1
                    ? "actividad"
                    : "actividades"}
                  .
                </Paragraph>
              )}

              {(ingestResult.skipped_activities ?? []).length > 0 && (
                <>
                  <Text type="secondary">
                    Actividades omitidas (sin cambios):
                  </Text>
                  <List
                    size="small"
                    dataSource={ingestResult.skipped_activities}
                    renderItem={(label) => (
                      <List.Item>
                        <Text>{label}</Text>
                      </List.Item>
                    )}
                  />
                </>
              )}
            </>
          ) : ingestResult.readingResourceId != null ||
            ingestResult.readingContentId != null ||
            ingestResult.programId != null ? (
            <>
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message={
                  <Text strong>
                    ¡Subida completada! Se creó la lectura primaria y quedó
                    vinculada al programa correspondiente.
                  </Text>
                }
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Space direction="vertical" style={{ marginBottom: 16 }}>
                {ingestResult.readingResourceId != null && (
                  <Text type="secondary">
                    ID de recurso:{" "}
                    <Text code>{ingestResult.readingResourceId}</Text>
                  </Text>
                )}
                {ingestResult.readingContentId != null && (
                  <Text type="secondary">
                    ID de contenido de lectura:{" "}
                    <Text code>{ingestResult.readingContentId}</Text>
                  </Text>
                )}
                {ingestResult.programId != null && (
                  <Text type="secondary">
                    ID de programa:{" "}
                    <Text code>{ingestResult.programId}</Text>
                  </Text>
                )}
              </Space>
            </>
          ) : ingestResult.mode === "replace" ? (
            <>
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message={
                  <Text strong>
                    {replacedSummary(
                      ingestResult.lessonsReplaced ??
                        ingestResult.lessonIds?.length ??
                        0,
                    )}
                  </Text>
                }
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Text type="secondary">IDs de lecciones sustituidas:</Text>
              <div style={{ marginTop: 8, marginBottom: 16 }}>
                {(ingestResult.lessonIds ?? []).map((id) => (
                  <Tag key={id} color="purple" style={{ marginBottom: 4 }}>
                    {id}
                  </Tag>
                ))}
              </div>
            </>
          ) : (
            <>
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message={
                  <Text strong>
                    ¡Subida completada! Se crearon{" "}
                    {ingestResult.lessonsCreated}{" "}
                    {ingestResult.lessonsCreated === 1
                      ? "lección"
                      : "lecciones"}
                    .
                  </Text>
                }
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Text type="secondary">IDs de lecciones creadas:</Text>
              <div style={{ marginTop: 8, marginBottom: 16 }}>
                {(ingestResult.lessonIds ?? []).map((id) => (
                  <Tag key={id} color="purple" style={{ marginBottom: 4 }}>
                    {id}
                  </Tag>
                ))}
              </div>
            </>
          )}

          {ingestResult.warnings && ingestResult.warnings.length > 0 && (
            <>
              <Divider />
              <Alert
                type="warning"
                message={`${ingestResult.warnings.length} advertencia(s) no bloqueante(s)`}
                showIcon
                style={{ marginBottom: 12 }}
              />
              <List
                size="small"
                dataSource={ingestResult.warnings}
                renderItem={(w) => (
                  <List.Item>
                    <Text type="warning">{w}</Text>
                  </List.Item>
                )}
              />
            </>
          )}
        </Card>
      )}

      {/* Prompts para IA */}
      <Divider />
      <Collapse
        ghost
        onChange={(keys) => {
          if (keys.includes("prompts") && !prompts && !loadingPrompts) {
            handleLoadPrompts();
          }
        }}
        items={[
          {
            key: "prompts",
            label: (
              <Space>
                <BulbOutlined style={{ color: token.colorWarning }} />
                <Text strong>Prompts para IA</Text>
                <Text type="secondary" style={{ fontWeight: "normal" }}>
                  — copia estos prompts en Claude o ChatGPT para corregir o
                  generar plantillas
                </Text>
              </Space>
            ),
            children: loadingPrompts ? (
              <Text type="secondary">Cargando prompts…</Text>
            ) : prompts ? (
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                {(
                  [
                    {
                      key: "corrector",
                      label: "Corrector de plantilla",
                      description:
                        "Pega este prompt en la IA seguido del contenido de tu plantilla. La IA la corregirá y listará los cambios realizados.",
                      value: prompts.corrector,
                    },
                    {
                      key: "generator",
                      label: "Generador de plantilla en blanco",
                      description:
                        "Completa los parámetros entre < > y pega el prompt en la IA. Recibirás una plantilla vacía lista para completar.",
                      value: prompts.generator,
                    },
                  ] as const
                ).map(({ key, label, description, value }) => (
                  <Card
                    key={key}
                    size="small"
                    title={
                      <Space>
                        <Text strong>{label}</Text>
                      </Space>
                    }
                    extra={
                      <Tooltip
                        title={
                          copiedKey === key ? "¡Copiado!" : "Copiar prompt"
                        }
                      >
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          type={copiedKey === key ? "primary" : "default"}
                          onClick={() => handleCopy(value, key)}
                        >
                          {copiedKey === key ? "Copiado" : "Copiar"}
                        </Button>
                      </Tooltip>
                    }
                  >
                    <Text
                      type="secondary"
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      {description}
                    </Text>
                    <Input.TextArea
                      value={value}
                      readOnly
                      autoSize={{ minRows: 4, maxRows: 12 }}
                      style={{ fontFamily: "monospace", fontSize: 12 }}
                    />
                  </Card>
                ))}
              </Space>
            ) : null,
          },
        ]}
      />

      <Modal
        open={appendPrompt !== null}
        title="Agregar lecciones a las que ya existen"
        onCancel={handleCancelAppend}
        footer={[
          <Button key="cancel" onClick={handleCancelAppend}>
            Cancelar
          </Button>,
          <Button key="replace" onClick={handleReplaceLessons}>
            Reemplazar (mismos IDs)
          </Button>,
          <Button key="append" type="primary" onClick={handleConfirmAppend}>
            Agregar
          </Button>,
        ]}
      >
        <Paragraph style={{ whiteSpace: "pre-line" }}>
          {appendPrompt ? targetFacts(appendPrompt) : null}
        </Paragraph>
        <Paragraph>
          Las lecciones de esta plantilla se van a agregar al final, sin
          modificar ni eliminar las que ya están cargadas.
        </Paragraph>
        <Paragraph>
          Con «Reemplazar (mismos IDs)», la plantilla sustituye el contenido de
          plataforma lección por lección, sin cambiar sus IDs.
        </Paragraph>
      </Modal>

      <ChangedInPlatformModal
        prompt={changedConflict?.prompt ?? null}
        canDownload={changedConflict?.courseId != null}
        onAnswer={(answer) => void handleChangedAnswer(answer)}
      />

      {/* ACTIVITIES_HAVE_ALTERNATIVES: one global decision for every
          conflicted activity — replace their existing sets with the doc's, or
          leave them untouched and process the rest. */}
      <Modal
        open={alternativesConflict !== null}
        title="Actividades con alternativas existentes"
        onCancel={handleCancelAlternativesConflict}
        footer={[
          <Button key="cancel" onClick={handleCancelAlternativesConflict}>
            Cancelar
          </Button>,
          <Button key="skip" onClick={handleSkipConflicted}>
            Omitir en conflicto
          </Button>,
          <Button
            key="replace"
            type="primary"
            danger
            onClick={handleReplaceExisting}
          >
            Reemplazar existentes
          </Button>,
        ]}
      >
        {alternativesConflict && alternativesConflict.activities.length > 0 ? (
          <>
            <Paragraph>
              Las siguientes actividades ya tienen alternativas configuradas:
            </Paragraph>
            <List
              size="small"
              dataSource={alternativesConflict.activities}
              renderItem={(a) => (
                <List.Item>
                  <Space>
                    <Text>{`Lección ${a.lesson_position} > "${a.activity_title}"`}</Text>
                    <Tag color="warning" icon={<WarningOutlined />}>
                      {a.existing_alternatives}{" "}
                      {a.existing_alternatives === 1
                        ? "existente"
                        : "existentes"}
                    </Tag>
                  </Space>
                </List.Item>
              )}
            />
            <Paragraph style={{ marginTop: 12, marginBottom: 8 }}>
              La decisión aplica a todas las actividades en conflicto por
              igual:
            </Paragraph>
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              <li>
                <Text strong>Reemplazar existentes:</Text> las alternativas
                actuales de esas actividades se sustituyen por las del
                documento.
              </li>
              <li>
                <Text strong>Omitir en conflicto:</Text> esas actividades se
                dejan sin cambios y solo se procesan las demás.
              </li>
            </ul>
          </>
        ) : (
          <Paragraph style={{ whiteSpace: "pre-line" }}>
            {alternativesConflict?.message}
          </Paragraph>
        )}
      </Modal>
    </div>
  );
};

export default SubidorSupersonico;
